import React, { useState, useRef, useEffect, useMemo, useContext } from 'react';
import { TruckLoader } from '../common/truck';
import { GoogleMap, Marker, MarkerF, PolylineF, InfoWindow, OverlayViewF, OverlayView, useJsApiLoader, DirectionsRenderer } from '@react-google-maps/api';
import { massLabel } from '../../utils/massCatalog';
import { orderHourlyText } from '../../utils/orderHourly';
import { useAuth } from '../../contexts/AuthContext';
import { ThemeContext } from '../../contexts/ThemeContext';
import { resolveMapTheme, mapStylesFor } from '../../utils/mapTheme';
import InvoiceBadge from '../common/InvoiceBadge';
import { curvedPath, spreadBubbles } from '../../utils/mapGeometry';

const OSLO_CENTER = { lat: 59.9139, lng: 10.7522 };
const LIBRARIES = ['places'];
const MAP_STYLE = { width: '100%', height: '100%' };

// Google Maps "night mode"-stil — mørk bakgrunn, dempede veinavn/labels,
// slik at kartet ikke lyser opp som en hvit flate når resten av appen er i mørk modus.
const DARK_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
  { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#181818' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
  { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#4e4e4e' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'transit', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] },
];

// Rutefarge for oppdrag. Valgt oppdrag vises sterkt; alle andre åpne oppdrag dempet.
const ROUTE_COLOR    = '#3b63d6';
const LOCATION_COLOR = '#1a73e8';
// Buede, prikkede ruter for åpne oppdrag: mørk blekk i lav opacity så kartet ikke forstyrres
const DOT_COLOR   = '#1a1a2e';
const DOT_OPACITY = 0.4;

// Prisbobler (HTML-overlegg) i stedet for Googles røde pinner
const pinStyle = {
  position: 'relative', border: 'none', cursor: 'pointer',
  background: '#fdc149', color: '#1a1a2e', borderRadius: 14, padding: '3px 10px',
  fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 18, lineHeight: 1.25,
  boxShadow: '0 2px 6px rgba(0,0,0,0.25)', whiteSpace: 'nowrap',
};
const bidPinStyle = { background: '#fff', border: '2px solid #fdc149', padding: '1px 9px' };
const selectedPinStyle = { background: '#1a1a2e', color: '#fdc149', border: 'none', padding: '3px 10px' };
const pinTip = {
  position: 'absolute', left: '50%', bottom: -4, width: 8, height: 8,
  background: '#fdc149', transform: 'translateX(-50%) rotate(45deg)', borderRadius: 2,
};

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const KEY_MISSING = !API_KEY || API_KEY === 'din-google-maps-nokkel';

export default function OrderMap({ orders, selectedOrder, onSelect, driverLocation, onMapLoad, selectedActiveOrder, fitTopPx = null, fitBottomPx = null }) {
  const [hovered, setHovered] = useState(null);
  const [directionsResult, setDirectionsResult] = useState(null);
  const mapRef = useRef(null);
  const initialLocateDone = useRef(false);
  const fittedOrdersRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const [zoom, setZoom] = useState(12);

  const routeOrder = selectedActiveOrder ?? selectedOrder ?? null;
  const routeKey   = routeOrder?.id ?? null;
  // Kartets farge velges på profilsiden (users.preferences.mapTheme: auto | light | dark).
  // «Følg appen» (standard) gir mørkt kart når appen er mørk.
  const { userDoc } = useAuth();
  const appTheme = useContext(ThemeContext)?.resolvedTheme ?? 'light';
  const mapPref = userDoc?.preferences?.mapTheme;
  const darkMap = resolveMapTheme(mapPref, appTheme) === 'dark';

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: KEY_MISSING ? '' : API_KEY,
    libraries: LIBRARIES,
  });

  // Case: driverLocation arrives after map is already loaded
  useEffect(() => {
    if (!driverLocation || !mapRef.current || initialLocateDone.current) return;
    initialLocateDone.current = true;
    mapRef.current.panTo(driverLocation);
  }, [driverLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedActiveOrder || !mapRef.current) return;
    const pos = selectedActiveOrder.pickupAddress;
    if (pos?.lat && pos?.lng) {
      mapRef.current.panTo({ lat: pos.lat, lng: pos.lng });
    }
  }, [selectedActiveOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Første gang: vis alle åpne oppdrag (og sjåføren) i stedet for bare Oslo sentrum.
  // Nedre halvdel dekkes av arket, så den holdes fri.
  useEffect(() => {
    if (!mapReady || fittedOrdersRef.current || !window.google || selectedOrder || selectedActiveOrder) return;
    const pts = orders
      .filter(o => o.pickupAddress?.lat && o.pickupAddress?.lng)
      .map(o => ({ lat: o.pickupAddress.lat, lng: o.pickupAddress.lng }));
    if (pts.length === 0) return;
    if (driverLocation) pts.push(driverLocation);
    const bounds = new window.google.maps.LatLngBounds();
    pts.forEach(p => bounds.extend(p));
    const h = mapRef.current.getDiv().clientHeight || 600;
    fittedOrdersRef.current = true;
    initialLocateDone.current = true;
    mapRef.current.fitBounds(bounds, { top: 110, left: 50, right: 50, bottom: Math.round(h * 0.5) });
  }, [mapReady, orders, driverLocation, selectedOrder, selectedActiveOrder]);

  // Valgt oppdrag: vis hele ruten i delen av kartet som ikke er dekket av detaljpanelet
  useEffect(() => {
    if (!directionsResult || !mapRef.current || !selectedOrder || selectedActiveOrder) return;
    const bounds = directionsResult.routes?.[0]?.bounds;
    if (!bounds) return;
    const h = mapRef.current.getDiv().clientHeight || 600;
    const bottom = fitBottomPx ?? (fitTopPx ? Math.max(40, h - fitTopPx + 20) : 80);
    mapRef.current.fitBounds(bounds, { top: 100, left: 40, right: 40, bottom });
  }, [directionsResult]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;

    if (!isLoaded || !routeOrder) {
      setDirectionsResult(null);
      return;
    }

    const pickup   = routeOrder.pickupAddress;
    const delivery = routeOrder.deliveryAddress;

    if (!pickup?.lat || !pickup?.lng || !delivery?.lat || !delivery?.lng) {
      setDirectionsResult(null);
      return;
    }

    const svc = new window.google.maps.DirectionsService();
    svc.route(
      {
        origin:      { lat: pickup.lat,   lng: pickup.lng   },
        destination: { lat: delivery.lat, lng: delivery.lng },
        travelMode:  window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (!cancelled) {
          setDirectionsResult(status === 'OK' ? result : null);
        }
      }
    );

    return () => { cancelled = true; };
  }, [routeKey, isLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prisbobler på samme sted (eller så nær at de overlapper ved dette zoomnivået) legges ved siden av hverandre
  const bubbleOffsets = useMemo(() => spreadBubbles(
    orders.filter(o => o.pickupAddress?.lat && o.pickupAddress?.lng)
      .map(o => ({ id: o.id, lat: o.pickupAddress.lat, lng: o.pickupAddress.lng })),
    zoom
  ), [orders, zoom]);

  function handleMapLoad(map) {
    mapRef.current = map;
    setMapReady(true);
    onMapLoad?.(map);
    if (driverLocation && !initialLocateDone.current) {
      initialLocateDone.current = true;
      map.panTo(driverLocation);
    }
  }

  if (KEY_MISSING) {
    return <MapPlaceholder msg="Google Maps API-nøkkel mangler i .env.local" info="Legg til REACT_APP_GOOGLE_MAPS_API_KEY og restart appen" />;
  }
  if (loadError) {
    return <MapPlaceholder msg="Kunne ikke laste Google Maps" info={loadError.message} />;
  }
  if (!isLoaded) {
    return <MapPlaceholder msg="Laster kart…" loading />;
  }

  // Boblen vises bare ved hover (desktop) — et valgt oppdrag viser kun ruten,
  // detaljene står allerede på kortet.
  const infoOrder = hovered?.id !== selectedOrder?.id ? hovered : null;
  const circle = window.google.maps.SymbolPath.CIRCLE;

  return (
    <GoogleMap
      mapContainerStyle={MAP_STYLE}
      center={OSLO_CENTER}
      zoom={12}
      options={{
        fullscreenControl: false, streetViewControl: false, mapTypeControl: false, gestureHandling: 'greedy',
        styles: mapStylesFor(mapPref, appTheme, DARK_MAP_STYLE),
      }}
      onLoad={handleMapLoad}
      onZoomChanged={() => { const z = mapRef.current?.getZoom(); if (z != null) setZoom(z); }}
      onClick={() => { setHovered(null); onSelect(null); }}
    >
      {/* Sjåførens posisjon — blå sirkel med hvit kant og en svak glorie rundt */}
      {driverLocation && (
        <>
          <MarkerF
            position={driverLocation}
            clickable={false}
            icon={{ path: circle, scale: 18, fillColor: LOCATION_COLOR, fillOpacity: 0.15, strokeWeight: 0 }}
            zIndex={99}
          />
          <MarkerF
            position={driverLocation}
            title="Din posisjon"
            icon={{ path: circle, scale: 8, fillColor: LOCATION_COLOR, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 3 }}
            zIndex={100}
          />
        </>
      )}

      {/* Dempet rute for hvert åpent oppdrag (unntatt det valgte, som får ekte kjørerute under).
          Rett linje henting → levering i stedet for kjørerute: Directions-kall for alle
          oppdrag ved hver kartvisning ville kostet per kall. Trykk på linjen velger oppdraget. */}
      {orders.map(order => {
        const p = order.pickupAddress;
        const d = order.deliveryAddress;
        if (!p?.lat || !p?.lng || !d?.lat || !d?.lng || selectedOrder?.id === order.id) return null;
        const path = curvedPath({ lat: p.lat, lng: p.lng }, { lat: d.lat, lng: d.lng });
        // Dempet på lyst kart, litt sterkere på mørkt for å synes
        const dotOpacity = darkMap ? 0.7 : DOT_OPACITY;
        return (
          <React.Fragment key={`route-${order.id}`}>
            <PolylineF
              path={path}
              options={{
                strokeOpacity: 0, strokeWeight: 12, geodesic: false, zIndex: 1,
                icons: [{
                  icon: { path: circle, scale: 1.6, fillColor: DOT_COLOR, fillOpacity: dotOpacity, strokeOpacity: 0 },
                  offset: '0', repeat: '9px',
                }],
              }}
              onClick={() => { onSelect(order); setHovered(null); }}
            />
            <MarkerF
              position={path[path.length - 1]}
              clickable={false}
              icon={{ path: circle, scale: 3, fillColor: DOT_COLOR, fillOpacity: dotOpacity + 0.1, strokeColor: '#ffffff', strokeWeight: 1 }}
              zIndex={2}
            />
          </React.Fragment>
        );
      })}

      {/* Aktivt oppdrag — hentingssted */}
      {selectedActiveOrder?.pickupAddress?.lat && (
        <Marker
          position={{ lat: selectedActiveOrder.pickupAddress.lat, lng: selectedActiveOrder.pickupAddress.lng }}
          title={`Henting: ${selectedActiveOrder.pickupAddress.street ?? ''}`}
          icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png' }}
          zIndex={200}
        />
      )}

      {/* Leveringssted for ordren ruten viser (aktivt eller valgt åpent oppdrag) —
          suppressMarkers på DirectionsRenderer under fjerner Googles egne
          standard-pins, så dette er det ENE markøren for leveringspunktet. */}
      {routeOrder?.deliveryAddress?.lat && (
        <Marker
          position={{ lat: routeOrder.deliveryAddress.lat, lng: routeOrder.deliveryAddress.lng }}
          title={`Levering: ${routeOrder.deliveryAddress.street ?? ''}`}
          icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' }}
          zIndex={200}
        />
      )}

      {/* Prisboble per åpent oppdrag: gul, valgt er mørk. Trykk velger oppdraget som i lista. */}
      {orders.map(order => {
        const pos = order.pickupAddress;
        if (!pos?.lat || !pos?.lng) return null;
        const isSelected = selectedOrder?.id === order.id;
        const hasPrice = order.fixedPrice != null && order.fixedPrice > 0;
        const off = bubbleOffsets.get(order.id) ?? null;
        return (
          <OverlayViewF
            key={order.id}
            position={{ lat: pos.lat, lng: pos.lng }}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            getPixelPositionOffset={(w, h) => ({ x: -w / 2 + (off?.dx ?? 0), y: -h - 6 + (off?.dy ?? 0) })}
            zIndex={isSelected ? 300 : 100}
          >
            <div style={{ position: 'relative' }}>
            {off && (
              /* Tynn linje fra den flyttede boblen til det ekte stedet */
              <svg aria-hidden="true" width="1" height="1" style={{ position: 'absolute', left: '50%', top: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                <line x1="0" y1="0" x2={-off.dx} y2={6 - off.dy} stroke={DOT_COLOR} strokeOpacity="0.5" strokeWidth="1" />
                <circle cx={-off.dx} cy={6 - off.dy} r="2.5" fill={DOT_COLOR} fillOpacity="0.6" />
              </svg>
            )}
            <button
              type="button"
              className="tippi-price-pin"
              aria-label={`${massLabel(order.massType)}, ${hasPrice ? `kr ${order.fixedPrice}` : 'åpent for bud'}`}
              aria-pressed={isSelected}
              onClick={e => { e.stopPropagation(); onSelect(isSelected ? null : order); setHovered(null); }}
              onMouseEnter={() => setHovered(order)}
              onMouseLeave={() => setHovered(null)}
              style={{
                ...pinStyle,
                ...(hasPrice ? {} : bidPinStyle),
                ...(isSelected ? selectedPinStyle : {}),
              }}
            >
              {hasPrice ? order.fixedPrice.toLocaleString('nb-NO') : 'Bud'}
              {!off && <span style={{ ...pinTip, ...(isSelected ? { background: '#1a1a2e' } : hasPrice ? {} : { background: '#fff', borderColor: '#fdc149' }) }} />}
            </button>
            </div>
          </OverlayViewF>
        );
      })}

      {directionsResult && (
        <DirectionsRenderer
          directions={directionsResult}
          options={{
            suppressMarkers: true,
            // Valgt oppdrag: kartet tilpasses selv (se effekten over), så ruten ikke havner bak detaljpanelet
            preserveViewport: !!selectedOrder && !selectedActiveOrder,
            polylineOptions: { strokeColor: ROUTE_COLOR, strokeOpacity: 0.9, strokeWeight: 5, zIndex: 10 },
          }}
        />
      )}

      {/* InfoWindow ved hover */}
      {infoOrder && infoOrder.pickupAddress?.lat && (
        <InfoWindow
          position={{ lat: infoOrder.pickupAddress.lat, lng: infoOrder.pickupAddress.lng }}
          onCloseClick={() => setHovered(null)}
          options={{ pixelOffset: { width: 0, height: -40 } }}
        >
          <div style={{ fontSize: 13, lineHeight: 1.5, maxWidth: 200 }}>
            <strong>{massLabel(infoOrder.massType)}</strong>
            {infoOrder.estimatedTons != null && <>{' · '}{infoOrder.estimatedTons} tonn</>}
            {orderHourlyText(infoOrder) && <>{' · '}{orderHourlyText(infoOrder)}</>}<br />
            <span style={{ color: '#666' }}>
              {infoOrder.pickupAddress?.street} → {infoOrder.deliveryAddress?.street}
            </span><br />
            {infoOrder.fixedPrice
              ? <strong>kr {infoOrder.fixedPrice},-</strong>
              : <span style={{ color: '#e67e22' }}>Anbudsrunde</span>
            }
            {infoOrder.paymentMethod === 'invoice' && <><br /><InvoiceBadge order={infoOrder} /></>}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  );
}

function MapPlaceholder({ msg, info, loading }) {
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: '#e8eaf0', color: '#555',
    }}>
      {loading
        ? <TruckLoader size={96} label={msg} decorative style={{ marginBottom: 16 }} />
        : <div style={{ fontSize: 48, marginBottom: 16 }}>🗺️</div>}
      <p role={loading ? 'status' : undefined} style={{ fontWeight: 600, fontSize: 16 }}>{msg}</p>
      {info && <p style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{info}</p>}
    </div>
  );
}
